import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing8, Spacing16 } from 'autogen/design-tokens/tokens';
import type { ProductReviewSummaryOverviewViewModel, ProductReviewSummaryViewModel } from 'autogen/swagger/all';
import { GetStaticImage } from 'autogen/swagger/all';
import { CustomersSays, MoreReviews } from 'autogen/translation-keys/trans-website-productreview';
import { AIExplainerButton } from 'autogen/translation-keys/trans-website-productreview-reviewreport';
import InlineButton from 'components/generic/InlineButton';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import { useQuickView } from 'components/quickView';
import dynamic from 'helpers/dynamic';
import type { IResponsiveImageModel } from 'shared/interfaces/responsiveImage';
import { breakpoint, sizes } from 'variables';
import AIExplainerQuickview from './components/AIExplainerQuickview';

// #region Styles
const Wrapper = styled.div`
    padding: ${Spacing16} 0;

    ${breakpoint.up(sizes.xl)} {
        padding-left: ${Spacing16};
        padding-right: ${Spacing16};
    }
`;

const Headline = styled(Typography.Body)`
    padding-bottom: ${Spacing16};
    margin-bottom: 0;
`;

const List = styled.ul`
    list-style: none;
    padding-left: ${Spacing16};
    margin-bottom: ${Spacing16};
`;

const ListItem = styled.li`
    display: flex;
    align-items: center;
`;

const IconContainer = styled.div`
    display: flex;
    align-self: center;
    margin-right: ${Spacing8};
    width: 16px;
    height: 16px;
`;

const QuickviewButton = styled(InlineButton)`
    display: flex;
    margin-bottom: ${Spacing16};
`;

const QuickviewButtonText = styled(Typography.Body)`
    opacity: 0.6;
    margin: 0;
`;

// #endregion Styles

function ReviewItem({ item, type }: { item: ProductReviewSummaryViewModel; type: 'positive' | 'negative' }) {
    const { amount, summary } = item;

    return (
        <ListItem>
            <IconContainer>
                {type === 'negative' ? <Icon type="Minus" size={16} /> : <Icon type="Plus" size={16} />}
            </IconContainer>
            <Typography.Body as="span">
                {summary} ({amount})
            </Typography.Body>
        </ListItem>
    );
}

export default function ReviewReport({
    neutralOrNegativeSummaries,
    positiveSummaries,
    ...restProps
}: ProductReviewSummaryOverviewViewModel) {
    const { t } = useTranslation();
    const { toggle } = useQuickView('AIExplainerQuickview');
    const [imageData, setImageData] = useState<IResponsiveImageModel>();

    function handleOpenQuickView() {
        GetStaticImage({ staticImageType: 'ReviewReport' }).then((res) => {
            setImageData(res);
        });
        toggle(true);
    }

    return (
        <Wrapper {...restProps}>
            <Headline size="L" strong>
                {t(CustomersSays)}
            </Headline>

            <List>
                {positiveSummaries.map((item, i) => (
                    <ReviewItem type="positive" item={item} key={i} />
                ))}
                {neutralOrNegativeSummaries.map((item, i) => (
                    <ReviewItem type="negative" item={item} key={i} />
                ))}
            </List>

            <QuickviewButton>
                <IconContainer>
                    <Icon type="AI" size={16} />
                </IconContainer>

                <QuickviewButtonText as="span" onClick={handleOpenQuickView}>
                    {t(AIExplainerButton)}
                </QuickviewButtonText>
            </QuickviewButton>

            <InlineButton as="a" href="#anchorReviews">
                <Typography.Body size="M" strong>
                    {t(MoreReviews)}
                </Typography.Body>
            </InlineButton>

            <AIExplainerQuickview imageData={imageData} />
        </Wrapper>
    );
}
